/**
 * Badge catalog — the static definition of every badge.
 *
 * This file is client-safe (pure data + pure functions, no server imports):
 * the app imports it directly to render names, descriptions, tiers, and to
 * resolve art via assetKey. Adding a badge is a code change here plus an
 * asset map entry in the app; nothing badge-specific is stored in the DB.
 *
 * Model: every badge is a counter with ascending tier thresholds. Tiered
 * badges are ONE badge (one userBadges row) whose achieved tier is derived
 * from how many thresholds `count` has passed. One-shots are `tiers: [1]`.
 *
 * See HOW_BADGES_WORK.md at the repo root for the full behavior spec.
 */

export type BadgeKind = 'sum' | 'max' | 'streak' | 'distinct_day'

export type BadgeCategory =
  | 'getting_started'
  | 'table_play'
  | 'hand_ranks'
  | 'lounges'
  | 'daily10'
  | 'cth'
  | 'dedication'
  // Reserved for features on other branches; no badges ship in these yet.
  | 'pocket_pick'
  | 'mtt'

export type BadgeEvent =
  | {
      type: 'hand_completed'
      playedDate: string // YYYY-MM-DD
      won: boolean
      isShowdown: boolean
      wonAllIn: boolean
      preflopFolded: boolean
      handRank?: string // engine rank name, e.g. 'Full House' (winners only)
      amount?: number // gross pot amount won (winners only)
      /** Net chips won: gross minus the winner's own bets this hand (winners only). */
      netAmount?: number
      localHour?: number // 0-23, client-local hour of the last human action
    }
  | { type: 'game_finished'; playedDate: string; isLoungeGame: boolean }
  | {
      type: 'daily10_completed'
      playedDate: string
      streak: number
      finalChips: number
      maxPossibleChips: number
    }
  | {
      type: 'cth_completed'
      playedDate: string
      roundsCorrect: number
      totalRounds: number
      totalElapsedMs: number
      currentStreak: number
    }
  | {
      type: 'pocket_pick_completed'
      playedDate: string
      roundsCorrect: number
      totalRounds: number
      totalElapsedMs: number
      currentStreak: number
    }
  | { type: 'profile_completed'; playedDate: string }
  | { type: 'lounge_joined'; playedDate: string }

export type BadgeEventType = BadgeEvent['type']

export interface BadgeDefinition {
  id: BadgeId
  name: string
  /**
   * Description template. `{x}` is resolved client-side to the current tier's
   * threshold when earned, or the first tier's threshold when locked.
   * One-shot descriptions are static.
   */
  description: string
  /** Override used when the resolved threshold is 1 (avoids "Play 1 hands"). */
  descriptionSingular?: string
  category: BadgeCategory
  /** Key into the app's badgeAssets component map. Explicit, not derived from id. */
  assetKey: string
  /** Ascending thresholds. One-shots are [1]. Earned tier = thresholds passed. */
  tiers: number[]
  kind: BadgeKind
  /** Events that can advance this badge (cheap pre-filter for the award engine). */
  events: BadgeEventType[]
  /**
   * Pure increment for an event. Meaning by kind:
   *  - sum: amount to add (0 = no-op)
   *  - max: candidate value for count (<= current count = no-op)
   *  - streak / distinct_day: nonzero = "this event counts toward the day logic"
   */
  increment: (event: BadgeEvent) => number
}

const countsWhen = (predicate: (event: BadgeEvent) => boolean) => (event: BadgeEvent) => (predicate(event) ? 1 : 0)

/** Win counted for a specific exact hand rank (a full house win does not bump Straight). */
const rankWin = (rank: string) => countsWhen((e) => e.type === 'hand_completed' && e.won && e.handRank === rank)

export const BADGE_IDS = [
  // 'profile_complete',
  'lounge_member',
  'lounge_games_played',
  'games_finished',
  'hands_played',
  'hands_won',
  'showdown_wins',
  'no_showdown_wins',
  'allin_wins',
  'biggest_pot_win',
  'bluff_catches_bluff',
  'preflop_folds',
  'rank_straight',
  'rank_flush',
  'rank_full_house',
  'rank_four_of_a_kind',
  'rank_straight_flush',
  'rank_royal_flush',
  'chips_won',
  'daily10_streak',
  'daily10_perfect',
  'daily10_played',
  'cth_perfect',
  'cth_speedrun',
  'cth_streak',
  'cth_played',
  'pp_perfect',
  'pp_speedrun',
  'pp_streak',
  'pp_played',
  'play_streak',
  'days_played',
  'night_owl',
  'early_bird'
] as const

export type BadgeId = (typeof BADGE_IDS)[number]

export const BADGES: Record<BadgeId, BadgeDefinition> = {
  // ── Getting Started ────────────────────────────────────────────────
  // "First X" achievements live as tier 1 (= 1) of their tiered badges below,
  // with descriptionSingular providing the first-time copy.
  // profile_complete: {
  //   id: 'profile_complete',
  //   name: 'Card-Carrying Member',
  //   description: 'Complete your profile',
  //   category: 'getting_started',
  //   assetKey: 'profile_complete',
  //   tiers: [1],
  //   kind: 'sum',
  //   events: ['profile_completed'],
  //   increment: countsWhen((e) => e.type === 'profile_completed')
  // },
  // ── Lounges ────────────────────────────────────────────────────────
  lounge_member: {
    id: 'lounge_member',
    name: 'Pull Up a Chair',
    description: 'Join a lounge',
    category: 'lounges',
    assetKey: 'lounge_member',
    tiers: [1],
    kind: 'sum',
    events: ['lounge_joined'],
    increment: countsWhen((e) => e.type === 'lounge_joined')
  },
  lounge_games_played: {
    id: 'lounge_games_played',
    name: 'Play With Friends',
    description: 'Play {x} lounge games',
    descriptionSingular: 'Play your first lounge game',
    category: 'lounges',
    assetKey: 'lounge_games_played',
    tiers: [1, 5, 15, 40],
    kind: 'sum',
    events: ['game_finished'],
    increment: countsWhen((e) => e.type === 'game_finished' && e.isLoungeGame)
  },

  // ── Table Play ─────────────────────────────────────────────────────
  games_finished: {
    id: 'games_finished',
    name: 'Full Session',
    description: 'Finish {x} games',
    descriptionSingular: 'Finish your first game',
    category: 'table_play',
    assetKey: 'games_finished',
    tiers: [1, 10, 50, 250],
    kind: 'sum',
    events: ['game_finished'],
    increment: countsWhen((e) => e.type === 'game_finished')
  },
  hands_played: {
    id: 'hands_played',
    name: 'Hands Played',
    description: 'Play {x} hands',
    descriptionSingular: 'Play your first hand of poker',
    category: 'table_play',
    assetKey: 'hands_played',
    tiers: [1, 100, 1000, 5000],
    kind: 'sum',
    events: ['hand_completed'],
    increment: countsWhen((e) => e.type === 'hand_completed')
  },
  // Win-family tiers are paced against each other (assuming ~25% of hands
  // won, ~half of wins at showdown, all-in wins ~5% of wins, high-card
  // showdown wins ~1%) and staggered so milestones don't land together.
  hands_won: {
    id: 'hands_won',
    name: 'Hands Won',
    description: 'Win {x} hands',
    descriptionSingular: 'Win your first hand',
    category: 'table_play',
    assetKey: 'hands_won',
    tiers: [1, 40, 400, 1500],
    kind: 'sum',
    events: ['hand_completed'],
    increment: countsWhen((e) => e.type === 'hand_completed' && e.won)
  },
  showdown_wins: {
    id: 'showdown_wins',
    name: 'Showdown Wins',
    description: 'Win {x} hands at showdown',
    category: 'table_play',
    assetKey: 'showdown_wins',
    tiers: [5, 25, 250, 900],
    kind: 'sum',
    events: ['hand_completed'],
    increment: countsWhen((e) => e.type === 'hand_completed' && e.won && e.isShowdown)
  },
  no_showdown_wins: {
    id: 'no_showdown_wins',
    name: "Fold 'Em",
    description: 'Win {x} hands without a showdown',
    category: 'table_play',
    assetKey: 'no_showdown_wins',
    tiers: [5, 30, 300, 1000],
    kind: 'sum',
    events: ['hand_completed'],
    increment: countsWhen((e) => e.type === 'hand_completed' && e.won && !e.isShowdown)
  },
  allin_wins: {
    id: 'allin_wins',
    name: 'All-In Wins',
    description: 'Win {x} hands after going all-in',
    descriptionSingular: 'Win a hand after going all-in',
    category: 'table_play',
    assetKey: 'allin_wins',
    tiers: [1, 5, 25, 75],
    kind: 'sum',
    events: ['hand_completed'],
    increment: countsWhen((e) => e.type === 'hand_completed' && e.won && e.wonAllIn)
  },
  biggest_pot_win: {
    id: 'biggest_pot_win',
    name: 'Biggest Pot Win',
    description: 'Win a single pot of {x} chips',
    category: 'table_play',
    assetKey: 'biggest_pot_win',
    tiers: [1000, 10000, 25000, 50000],
    kind: 'max',
    events: ['hand_completed'],
    increment: (e) => (e.type === 'hand_completed' && e.won ? (e.amount ?? 0) : 0)
  },
  bluff_catches_bluff: {
    id: 'bluff_catches_bluff',
    name: 'Bluff Catches Bluff',
    // Winning a showdown with just High Card means every caller showed even
    // less — they were bluffing and you caught them.
    description: 'Win {x} showdowns with just a high card',
    descriptionSingular: 'Win a showdown with just a high card',
    category: 'table_play',
    assetKey: 'bluff_catches_bluff',
    tiers: [1, 3, 8, 20],
    kind: 'sum',
    events: ['hand_completed'],
    increment: countsWhen((e) => e.type === 'hand_completed' && e.won && e.isShowdown && e.handRank === 'High Card')
  },
  preflop_folds: {
    id: 'preflop_folds',
    name: 'Disciplined',
    description: 'Fold {x} hands before the flop',
    category: 'table_play',
    assetKey: 'preflop_folds',
    tiers: [10, 150, 750, 2500],
    kind: 'sum',
    events: ['hand_completed'],
    increment: countsWhen((e) => e.type === 'hand_completed' && e.preflopFolded)
  },

  // ── Hand Ranks (each counts wins at exactly that rank) ─────────────
  rank_straight: {
    id: 'rank_straight',
    name: 'Straight',
    description: 'Win {x} hands with a straight',
    category: 'hand_ranks',
    assetKey: 'rank_straight',
    tiers: [5, 25, 100, 250],
    kind: 'sum',
    events: ['hand_completed'],
    increment: rankWin('Straight')
  },
  rank_flush: {
    id: 'rank_flush',
    name: 'Flush',
    description: 'Win {x} hands with a flush',
    category: 'hand_ranks',
    assetKey: 'rank_flush',
    tiers: [5, 25, 75, 200],
    kind: 'sum',
    events: ['hand_completed'],
    increment: rankWin('Flush')
  },
  rank_full_house: {
    id: 'rank_full_house',
    name: 'Full House',
    description: 'Win {x} hands with a full house',
    category: 'hand_ranks',
    assetKey: 'rank_full_house',
    tiers: [3, 15, 50, 150],
    kind: 'sum',
    events: ['hand_completed'],
    increment: rankWin('Full House')
  },
  rank_four_of_a_kind: {
    id: 'rank_four_of_a_kind',
    name: 'Four of a Kind',
    description: 'Win {x} hands with four of a kind',
    descriptionSingular: 'Win a hand with four of a kind',
    category: 'hand_ranks',
    assetKey: 'rank_four_of_a_kind',
    tiers: [1, 5, 15, 50],
    kind: 'sum',
    events: ['hand_completed'],
    increment: rankWin('Four of a Kind')
  },
  rank_straight_flush: {
    id: 'rank_straight_flush',
    name: 'Straight Flush',
    description: 'Win {x} hands with a straight flush',
    descriptionSingular: 'Win a hand with a straight flush',
    category: 'hand_ranks',
    assetKey: 'rank_straight_flush',
    tiers: [1, 3, 10, 25],
    kind: 'sum',
    events: ['hand_completed'],
    increment: rankWin('Straight Flush')
  },
  rank_royal_flush: {
    id: 'rank_royal_flush',
    name: 'Royal Flush',
    description: 'Win {x} hands with a royal flush',
    descriptionSingular: 'Win a hand with a royal flush',
    category: 'hand_ranks',
    assetKey: 'rank_royal_flush',
    tiers: [1, 2, 3, 4],
    kind: 'sum',
    events: ['hand_completed'],
    increment: rankWin('Royal Flush')
  },

  chips_won: {
    id: 'chips_won',
    name: 'Chips Won',
    description: 'Win {x} chips in total',
    category: 'table_play',
    assetKey: 'chips_won',
    tiers: [50000, 100000, 500000, 1000000],
    kind: 'sum',
    events: ['hand_completed'],
    // Net winnings only (gross minus own blinds+bets) — biggest_pot_win uses gross.
    increment: (e) => (e.type === 'hand_completed' && e.won ? (e.netAmount ?? 0) : 0)
  },

  // ── Daily 10 ───────────────────────────────────────────────────────
  daily10_streak: {
    id: 'daily10_streak',
    name: 'Daily 10 Streak',
    description: 'Complete the Daily 10 {x} days in a row',
    category: 'daily10',
    assetKey: 'daily10_streak',
    tiers: [3, 7, 14, 30],
    kind: 'max',
    events: ['daily10_completed'],
    increment: (e) => (e.type === 'daily10_completed' ? e.streak : 0)
  },
  daily10_perfect: {
    id: 'daily10_perfect',
    name: 'Perfect Day',
    description: 'Finish a Daily 10 with the maximum possible chips, {x} times',
    descriptionSingular: 'Finish a Daily 10 with the maximum possible chips',
    category: 'daily10',
    assetKey: 'daily10_perfect',
    tiers: [1, 5, 15, 30],
    kind: 'sum',
    events: ['daily10_completed'],
    increment: countsWhen((e) => e.type === 'daily10_completed' && e.finalChips >= e.maxPossibleChips)
  },
  daily10_played: {
    id: 'daily10_played',
    name: 'Daily Regular',
    description: 'Complete {x} Daily 10s',
    descriptionSingular: 'Complete your first Daily 10',
    category: 'daily10',
    assetKey: 'daily10_played',
    tiers: [1, 10, 50, 150],
    kind: 'sum',
    events: ['daily10_completed'],
    increment: countsWhen((e) => e.type === 'daily10_completed')
  },

  // ── Complete the Hand ──────────────────────────────────────────────
  cth_perfect: {
    id: 'cth_perfect',
    name: 'Perfect Read',
    description: 'Get every round right in Complete the Hand, {x} times',
    descriptionSingular: 'Get every round right in Complete the Hand',
    category: 'cth',
    assetKey: 'cth_perfect',
    tiers: [1, 5, 10, 50],
    kind: 'sum',
    events: ['cth_completed'],
    increment: countsWhen((e) => e.type === 'cth_completed' && e.totalRounds > 0 && e.roundsCorrect >= e.totalRounds)
  },
  cth_speedrun: {
    id: 'cth_speedrun',
    name: 'Speed Reader',
    description: 'Complete {x} perfect Complete the Hands in under a minute each',
    descriptionSingular: 'Complete a perfect Complete the Hand in under a minute',
    category: 'cth',
    assetKey: 'cth_speedrun',
    tiers: [1, 3, 8, 20],
    kind: 'sum',
    events: ['cth_completed'],
    increment: countsWhen(
      (e) =>
        e.type === 'cth_completed' && e.totalRounds > 0 && e.roundsCorrect >= e.totalRounds && e.totalElapsedMs < 60_000
    )
  },
  cth_streak: {
    id: 'cth_streak',
    name: 'CTH Streak',
    description: 'Complete the Hand {x} days in a row',
    category: 'cth',
    assetKey: 'cth_streak',
    tiers: [3, 7, 30, 90],
    kind: 'max',
    events: ['cth_completed'],
    increment: (e) => (e.type === 'cth_completed' ? e.currentStreak : 0)
  },
  cth_played: {
    id: 'cth_played',
    name: 'Daily Reader',
    description: 'Complete {x} Complete the Hand games',
    descriptionSingular: 'Complete your first Complete the Hand',
    category: 'cth',
    assetKey: 'cth_played',
    tiers: [1, 10, 50, 150],
    kind: 'sum',
    events: ['cth_completed'],
    increment: countsWhen((e) => e.type === 'cth_completed')
  },

  // ── Pocket Pick (mirrors Complete the Hand) ───────────────────────
  pp_perfect: {
    id: 'pp_perfect',
    name: 'Perfect Pick',
    description: 'Pick the best card every round in Pocket Pick, {x} times',
    descriptionSingular: 'Pick the best card every round in Pocket Pick',
    category: 'pocket_pick',
    assetKey: 'pp_perfect',
    tiers: [1, 5, 10, 50],
    kind: 'sum',
    events: ['pocket_pick_completed'],
    increment: countsWhen(
      (e) => e.type === 'pocket_pick_completed' && e.totalRounds > 0 && e.roundsCorrect >= e.totalRounds
    )
  },
  pp_speedrun: {
    id: 'pp_speedrun',
    name: 'Quick Draw',
    description: 'Complete {x} perfect Pocket Picks in under a minute each',
    descriptionSingular: 'Complete a perfect Pocket Pick in under a minute',
    category: 'pocket_pick',
    assetKey: 'pp_speedrun',
    tiers: [1, 3, 8, 20],
    kind: 'sum',
    events: ['pocket_pick_completed'],
    increment: countsWhen(
      (e) =>
        e.type === 'pocket_pick_completed' &&
        e.totalRounds > 0 &&
        e.roundsCorrect >= e.totalRounds &&
        e.totalElapsedMs < 60_000
    )
  },
  pp_streak: {
    id: 'pp_streak',
    name: 'Pocket Pick Streak',
    description: 'Play Pocket Pick {x} days in a row',
    category: 'pocket_pick',
    assetKey: 'pp_streak',
    tiers: [3, 7, 30, 90],
    kind: 'max',
    events: ['pocket_pick_completed'],
    increment: (e) => (e.type === 'pocket_pick_completed' ? e.currentStreak : 0)
  },
  pp_played: {
    id: 'pp_played',
    name: 'Daily Picker',
    description: 'Complete {x} Pocket Pick games',
    descriptionSingular: 'Complete your first Pocket Pick',
    category: 'pocket_pick',
    assetKey: 'pp_played',
    tiers: [1, 10, 50, 150],
    kind: 'sum',
    events: ['pocket_pick_completed'],
    increment: countsWhen((e) => e.type === 'pocket_pick_completed')
  },

  // ── Dedication ─────────────────────────────────────────────────────
  play_streak: {
    id: 'play_streak',
    name: 'Play Streak',
    description: 'Play {x} days in a row',
    category: 'dedication',
    assetKey: 'play_streak',
    tiers: [3, 7, 14, 30],
    kind: 'streak',
    events: ['hand_completed', 'daily10_completed', 'cth_completed', 'pocket_pick_completed'],
    increment: () => 1
  },
  days_played: {
    id: 'days_played',
    name: 'Days Played',
    description: 'Play on {x} different days',
    category: 'dedication',
    assetKey: 'days_played',
    tiers: [7, 30, 90, 365],
    kind: 'distinct_day',
    events: ['hand_completed', 'daily10_completed', 'cth_completed', 'pocket_pick_completed'],
    increment: () => 1
  },
  night_owl: {
    id: 'night_owl',
    name: 'Night Owl',
    description: 'Play {x} hands after midnight',
    descriptionSingular: 'Play a hand after midnight',
    category: 'dedication',
    assetKey: 'night_owl',
    tiers: [1, 10, 50, 150],
    kind: 'sum',
    events: ['hand_completed'],
    increment: countsWhen((e) => e.type === 'hand_completed' && e.localHour !== undefined && e.localHour < 5)
  },
  early_bird: {
    id: 'early_bird',
    name: 'Early Bird',
    description: 'Play {x} hands before 8am',
    descriptionSingular: 'Play a hand before 8am',
    category: 'dedication',
    assetKey: 'early_bird',
    tiers: [1, 10, 50, 150],
    kind: 'sum',
    events: ['hand_completed'],
    increment: countsWhen(
      (e) => e.type === 'hand_completed' && e.localHour !== undefined && e.localHour >= 5 && e.localHour < 8
    )
  }

  // ── Future categories ──────────────────────────────────────────────
  // mtt: when the feat/mtt branch merges, add badges here (e.g. first
  // tournament, final tables, tournament wins) and fire 'mtt_finished' from
  // the tournament orchestrator. The category already exists in
  // BadgeCategory/BADGE_CATEGORY_ORDER; the grid skips empty categories.
}

export const BADGE_COUNT = BADGE_IDS.length

export function isBadgeId(value: string): value is BadgeId {
  return Object.hasOwn(BADGES, value)
}

const ALL_EVENT_TYPES: BadgeEventType[] = [
  'hand_completed',
  'game_finished',
  'daily10_completed',
  'cth_completed',
  'pocket_pick_completed',
  'profile_completed',
  'lounge_joined'
]

/** Badges subscribed to each event type. Built once at module load. */
export const BADGES_BY_EVENT: Record<BadgeEventType, BadgeDefinition[]> = ALL_EVENT_TYPES.reduce(
  (acc, eventType) => {
    acc[eventType] = Object.values(BADGES).filter((badge) => badge.events.includes(eventType))
    return acc
  },
  {} as Record<BadgeEventType, BadgeDefinition[]>
)

/** Display order for the badges page, grouped by category. */
export const BADGE_CATEGORY_ORDER: BadgeCategory[] = [
  'dedication',
  'getting_started',
  'table_play',
  'hand_ranks',
  'lounges',
  'daily10',
  'cth',
  'pocket_pick',
  'mtt'
]

export const BADGE_CATEGORY_LABELS: Record<BadgeCategory, string> = {
  getting_started: 'Getting Started',
  table_play: 'Table Play',
  hand_ranks: 'Hand Ranks',
  lounges: 'Lounges',
  daily10: 'Daily 10',
  cth: 'Complete the Hand',
  dedication: 'Dedication',
  pocket_pick: 'Pocket Pick',
  mtt: 'Tournaments'
}

/** Tier names by position from the END of the tiers array: the top tier of any badge is always platinum. */
export const TIER_NAMES = ['Bronze', 'Silver', 'Gold', 'Platinum'] as const
export type TierName = (typeof TIER_NAMES)[number]

/**
 * Name for an earned tier (1-based) of a badge. Badges with fewer than four
 * tiers use the LAST N names, e.g. a 3-tier badge runs Silver → Gold → Platinum.
 * One-shots return Platinum but are typically rendered without a tier label.
 */
export function tierName(badge: BadgeDefinition, tier: number): TierName {
  const offset = TIER_NAMES.length - badge.tiers.length
  return TIER_NAMES[Math.min(offset + tier - 1, TIER_NAMES.length - 1)]
}
